import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Zeichen, type ZeichenName } from './Zeichen.js'

/**
 * Die Befehlszeile – ⌘K.
 *
 * Der Grund für dieses Ding ist kein Geschmack, sondern Zeit. Wer im Büro
 * arbeitet, weiss in neun von zehn Fällen genau, wohin er will: zu den
 * offenen Rechnungen, zu einem Kunden, zu einer neuen Rechnung. Bis jetzt
 * musste er dafür die Seitenleiste lesen, obwohl er nichts suchen wollte.
 * Hier tippt er «rech» und ist da.
 *
 * Zwei Dinge, die eine Befehlszeile schlecht machen, sind hier vermieden:
 *
 *   Sie zeigt nichts an, was nicht geht. Jeder Eintrag, der hier steht, führt
 *   irgendwohin – ein Befehl, der die Fläche nur schliesst, ist eine Attrappe
 *   und verbrennt das Vertrauen in alle anderen.
 *
 *   Sie sucht so, wie jemand tippt. «rech neu» findet «Neue Rechnung», auch
 *   wenn die Wörter in der anderen Reihenfolge stehen. Ein Filter, der nur
 *   von vorn passt, zwingt zum Raten der genauen Bezeichnung – und genau das
 *   sollte die Befehlszeile abnehmen.
 */

export interface Befehl {
  /** Eindeutig, damit React die Liste nicht durcheinanderbringt. */
  id: string
  /** Was dasteht. Danach wird gesucht. */
  wort: string
  /** Die Gruppe, unter der er einsortiert wird. */
  gruppe: string
  icon?: ZeichenName
  /** Ein Zusatz rechts – etwa eine Anzahl. Nie erfunden. */
  zusatz?: string
  tun: () => void
}

/**
 * Sucht wie ein Mensch tippt: jedes Wort muss irgendwo vorkommen, die
 * Reihenfolge ist egal, Gross- und Kleinschreibung auch.
 */
export function passt(text: string, suche: string): boolean {
  const t = text.toLocaleLowerCase('de-CH')
  return suche
    .toLocaleLowerCase('de-CH')
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => t.includes(w))
}

export function Befehlszeile({
  befehle,
  offen,
  aufSchliessen
}: {
  befehle: Befehl[]
  offen: boolean
  aufSchliessen: () => void
}): JSX.Element | null {
  const [text, setText] = useState('')
  const [wahl, setWahl] = useState(0)
  const feld = useRef<HTMLInputElement>(null)
  const listeRef = useRef<HTMLDivElement>(null)

  const treffer = useMemo(
    () => (text.trim() ? befehle.filter((b) => passt(`${b.wort} ${b.gruppe}`, text)) : befehle),
    [befehle, text]
  )

  // Bei jedem Öffnen von vorn: ein Feld, in dem noch die Suche von gestern
  // steht, ist schlimmer als ein leeres.
  useEffect(() => {
    if (!offen) return
    setText('')
    setWahl(0)
    feld.current?.focus()
  }, [offen])

  // Die Markierung darf nicht ins Leere zeigen, wenn die Liste kürzer wird.
  useEffect(() => {
    setWahl((w) => (w >= treffer.length ? Math.max(0, treffer.length - 1) : w))
  }, [treffer.length])

  const ausfuehren = useCallback(
    (b: Befehl | undefined) => {
      if (!b) return
      aufSchliessen()
      b.tun()
    },
    [aufSchliessen]
  )

  useEffect(() => {
    if (!offen) return
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        aufSchliessen()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setWahl((w) => (treffer.length ? (w + 1) % treffer.length : 0))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setWahl((w) => (treffer.length ? (w - 1 + treffer.length) % treffer.length : 0))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        ausfuehren(treffer[wahl])
      }
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [offen, treffer, wahl, ausfuehren, aufSchliessen])

  // Die markierte Zeile bleibt sichtbar, auch wenn man mit der Pfeiltaste
  // über den Rand der Liste hinausläuft.
  useEffect(() => {
    listeRef.current?.querySelector('[data-wahl]')?.scrollIntoView({ block: 'nearest' })
  }, [wahl])

  if (!offen) return null

  let letzteGruppe: string | null = null

  return (
    <div
      className="rp-bz"
      role="dialog"
      aria-modal="true"
      aria-label="Befehl"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aufSchliessen()
      }}
    >
      <div className="rp-bz-feld-halter">
        <div className="rp-bz-feld">
          <Zeichen name="suche" groesse={18} />
          <input
            ref={feld}
            id="befehl-eingabe"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="Was möchtest du tun?"
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setWahl(0)
            }}
            aria-controls="befehl-liste"
            aria-expanded={treffer.length > 0}
          />
          <kbd>ESC</kbd>
        </div>

        <div className="rp-bz-liste" id="befehl-liste" role="listbox" ref={listeRef}>
          {treffer.length === 0 && (
            <p className="rp-bz-leer">Nichts gefunden. Andere Wörter probieren.</p>
          )}

          {treffer.map((b, i) => {
            const kopf = b.gruppe !== letzteGruppe ? b.gruppe : null
            letzteGruppe = b.gruppe
            return (
              <div key={b.id}>
                {kopf && <div className="rp-bz-gruppe">{kopf}</div>}
                <div
                  className="rp-bz-zeile"
                  role="option"
                  aria-selected={i === wahl}
                  data-wahl={i === wahl ? '' : undefined}
                  onMouseMove={() => setWahl(i)}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    ausfuehren(b)
                  }}
                >
                  <Zeichen name={b.icon ?? 'mehr'} groesse={17} />
                  <b>{b.wort}</b>
                  {b.zusatz && <em>{b.zusatz}</em>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * ⌘K beziehungsweise Strg-K, überall.
 *
 * Der Haken liegt hier und nicht in der Fläche selbst: Sonst müsste die
 * Fläche dauernd im Aufbau stehen, nur um auf eine Taste zu warten.
 */
export function useBefehlstaste(aufUmschalten: () => void): void {
  useEffect(() => {
    const beiTaste = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        aufUmschalten()
      }
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [aufUmschalten])
}
